import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Info, ShieldAlert } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { installWebFilter, orchestrateNativeWebFilter } from "@/lib/native/webFilter";

import { OnboardingIllustration } from "../OnboardingIllustration";
import { OnboardingVibesOverlay } from "../OnboardingVibesOverlay";
import { getOnboardingIllustrationDimensions } from "../onboardingImagePreload";
import { onboardingImageCandidates } from "../onboardingImageUrl";
import { useOptionalOnboardingAnalytics } from "@/lib/onboarding/OnboardingAnalyticsContext";

export function VpnSetupSlide({ error }: { error?: string | null }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const isNative = Capacitor.isNativePlatform();
  const isAndroid = Capacitor.getPlatform() === "android";
  const dimensions = getOnboardingIllustrationDimensions(10);

  return (
    <div className="flex flex-1 flex-col items-center gap-6 pb-4">
      <div className="relative mx-auto w-fit max-w-[280px]">
        <OnboardingVibesOverlay variant="setup" />
        <OnboardingIllustration
          candidates={onboardingImageCandidates(10)}
          alt={t.onboarding.vpnImageAlt}
          intrinsicWidth={dimensions?.width}
          intrinsicHeight={dimensions?.height}
        />
      </div>

      <div className="flex w-full max-w-md flex-col gap-4 text-center">
        <h2 className="text-xl font-semibold leading-snug text-[oklch(92%_0.02_150)]">
          {t.onboarding.vpnTitle}
        </h2>
        <p className="text-base leading-7 text-[oklch(82%_0.025_155)]">
          {isAndroid ? t.onboarding.vpnBodyAndroid : t.onboarding.vpnBody}
        </p>
      </div>

      <section className="liquid-glass-suggestion flex w-full max-w-md gap-3 p-4 text-left">
        <Info size={20} className="mt-0.5 shrink-0 text-[oklch(78%_0.06_155)]" aria-hidden />
        <div className="grid gap-1">
          <p className="text-sm font-semibold text-[oklch(90%_0.02_150)]">
            {t.onboarding.vpnPrivacyTitle}
          </p>
          <p className="text-sm leading-6 text-[oklch(80%_0.025_155)]">
            {t.onboarding.vpnPrivacyBody}
          </p>
        </div>
      </section>

      {error ? (
        <section className="liquid-glass-alert-warning flex w-full max-w-md flex-col gap-2 p-4 text-left text-[oklch(82%_0.055_85)]">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="shrink-0" aria-hidden />
            <p className="text-sm font-semibold">{t.onboarding.vpnErrorTitle}</p>
          </div>
          <p className="text-xs leading-5 text-[oklch(78%_0.04_85)]">{t.onboarding.vpnErrorHint}</p>
          {/* Raw native reason, shown verbatim for on-device diagnosis (no logs needed). */}
          <p className="break-words font-mono text-[11px] leading-4 text-[oklch(70%_0.03_85)]">
            {error}
          </p>
        </section>
      ) : null}

      {!isNative ? (
        <p className="text-center text-sm text-[oklch(68%_0.025_155)]">
          {t.onboarding.vpnWebHint}
        </p>
      ) : null}
    </div>
  );
}

export function useVpnSetupSlideAction(onAdvance: () => void, options?: { step?: number }) {
  const analyticsStep = options?.step ?? 7;
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const isNative = Capacitor.isNativePlatform();
  const analytics = useOptionalOnboardingAnalytics();
  const [vpnBusy, setVpnBusy] = useState(false);
  const [vpnError, setVpnError] = useState<string | null>(null);

  async function handleVpnCta() {
    if (vpnBusy) return;
    analytics?.trackCtaClick(analyticsStep, "primary");

    if (!isNative) {
      analytics?.trackPermissionOutcome(analyticsStep, "vpn", "skipped_web");
      onAdvance();
      return;
    }
    setVpnBusy(true);
    setVpnError(null);
    try {
      // Triggers the iOS "…wants to add VPN configurations" system prompt. On Allow,
      // saves the profile with the tunnel off (no status-bar VPN icon yet).
      const result = await installWebFilter();
      if (!result.installed) {
        analytics?.trackPermissionOutcome(analyticsStep, "vpn", "denied", {
          reason: result.reason ?? result.status,
        });
        setVpnError(result.reason ?? result.status);
        return;
      }
      analytics?.trackPermissionOutcome(analyticsStep, "vpn", "granted");
      // Ensure tunnel stays off when no enforcing web-blocking rule exists yet.
      await orchestrateNativeWebFilter([], []);
      onAdvance();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      analytics?.trackPermissionOutcome(analyticsStep, "vpn", "error", { reason: message });
      setVpnError(message);
    } finally {
      setVpnBusy(false);
    }
  }

  function handleSkip() {
    analytics?.trackCtaClick(analyticsStep, "secondary");
    analytics?.trackPermissionOutcome(analyticsStep, "vpn", "skipped");
    onAdvance();
  }

  return {
    handleVpnCta,
    handleSkip,
    ctaLabel: isNative ? t.onboarding.vpnInstall : t.onboarding.continue,
    secondaryCtaLabel: t.onboarding.vpnNotNow,
    vpnBusy,
    vpnError,
  };
}
